// 🔒 LOCKED: Extras & Credits system
// Do not modify structure, UI, or logic without review

import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import useTranslation from '../../../hooks/useTranslation';
import EntityDocumentsModal from './EntityDocumentsModal';
import { listCounts as listDocumentCounts } from '../../../services/entityDocumentsService';
import ExtraNotesButton from './ExtraNotesButton';
import { listSummaryByProject as listNotesSummary } from '../../../services/extraNotesService';
import { supabase } from '../../../lib/supabase';
import PortalDropdown from '../../../components/ui/PortalDropdown';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import { formatCurrency as fmtCAD } from '../../../utils/formatCurrency';
import { statusVariant } from '../../../utils/statusVariant';
import Badge, { BADGE_VARIANTS } from '../../../components/ui/Badge';
import { formatDate } from '../../../utils/formatDate';
import { getCurrentOrgName } from '../../../lib/getCurrentOrgId';
import { projectExtrasService, isCoRequired } from '../../../services/projectExtrasService';

// Status display mapping layer - translates for display only, does NOT modify DB values
const getStatusConfig = (t) => ({
  draft: { label: t('status.draft'), bg: 'bg-gray-100', text: 'text-gray-600' },
  submitted: { label: t('status.submitted'), bg: 'bg-blue-100', text: 'text-blue-700' },
  to_resubmit: { label: t('status.to_resubmit'), bg: 'bg-amber-100', text: 'text-amber-700' },
  approved: { label: t('status.approved'), bg: 'bg-green-100', text: 'text-green-700' },
  rejected: { label: t('status.rejected'), bg: 'bg-red-100', text: 'text-red-700' }
});

// Type display mapping layer - translates for display only, does NOT modify DB values
const getTypeConfig = (t) => ({
  extra: { label: t('extras.types.extra'), bg: 'bg-green-100', text: 'text-green-700' },
  credit: { label: t('extras.types.credit'), bg: 'bg-red-100', text: 'text-red-700' }
});

// Category badge config
const getCategoryConfig = (t) => ({
  contractual:    { label: t('extras.category.contractual'),    bg: 'bg-gray-100',   text: 'text-gray-600'   },
  backcharge:     { label: t('extras.category.backcharge'),     bg: 'bg-blue-100',   text: 'text-blue-700'   },
  internal_error: { label: t('extras.category.internal_error'), bg: 'bg-red-100',    text: 'text-red-700'    },
  absorbed_tm:    { label: t('extras.category.absorbed_tm'),    bg: 'bg-orange-100', text: 'text-orange-700' },
  dispute:        { label: t('extras.category.dispute'),        bg: 'bg-purple-100', text: 'text-purple-700' },
});

// « A resoumettre » : le client a repondu, l'extra doit etre corrige puis resoumis.
const STATUS_OPTIONS = ['draft', 'submitted', 'to_resubmit', 'approved', 'rejected'];

const SortIcon = ({ column, sortConfig }) => {
  if (sortConfig?.key !== column) {
    return <Icon name="ChevronsUpDown" size={12} className="text-gray-400" />;
  }
  return sortConfig?.direction === 'asc'
    ? <Icon name="ChevronUp" size={12} className="text-primary" />
    : <Icon name="ChevronDown" size={12} className="text-primary" />;
};

// 3-dot actions dropdown menu — portal-based, never clips inside table overflow
const ActionsMenu = ({ extra, onEdit, onDelete, onViewDocuments, onDownloadPDF, t }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);

  const isApproved = extra?.status === 'approved';

  const items = [
    {
      label: t('common.edit'),
      icon: 'Pencil',
      onClick: () => onEdit?.(extra),
      disabled: isApproved,
      title: isApproved ? t('extras.locked') : undefined,
    },
    {
      label: t('extras.downloadPdf'),
      icon: 'FileDown',
      onClick: () => onDownloadPDF?.(extra),
    },
    {
      label: t('common.delete'),
      icon: 'Trash2',
      onClick: () => onDelete(extra?.id),
      variant: isApproved ? undefined : 'danger',
      disabled: isApproved,
      title: isApproved ? t('extras.locked') : undefined,
    },
  ];

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center justify-center w-7 h-7 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
        title={t('extras.rowActions.actions')}
      >
        <Icon name="MoreVertical" size={15} />
      </button>
      <PortalDropdown
        anchorRef={btnRef}
        open={open}
        onClose={() => setOpen(false)}
        items={items}
        width={200}
      />
    </>
  );
};

// ─── DocumentIconButton ────────────────────────────────────────────────────────
// Compteur et noms viennent de la table extra_documents, lue UNE fois pour tout
// le tableau (plus aucun listage Storage par ligne).
const DocumentIconButton = ({ summary, onClick }) => {
  const { t } = useTranslation();
  const docCount = summary?.count || 0;
  const docNames = summary?.names || [];
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipTimeout = useRef(null);

  const handleMouseEnter = () => {
    tooltipTimeout.current = setTimeout(() => setShowTooltip(true), 200);
  };

  const handleMouseLeave = () => {
    clearTimeout(tooltipTimeout?.current);
    setShowTooltip(false);
  };

  const hasDocuments = docCount > 0;

  return (
    <div className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <button
        onClick={onClick}
        className="relative p-1 rounded transition-colors group"
        title={t('extras.rowActions.documents')}
      >
        <Icon
          name="Paperclip"
          size={13}
          className={`transition-colors ${hasDocuments ? 'text-orange-500' : 'text-gray-400'} group-hover:text-gray-700`}
        />
        {/* Badge */}
        <span
          className={`absolute -top-1 -right-1 text-[10px] rounded-full px-1 leading-tight font-medium ${
            hasDocuments ? 'bg-orange-100 text-orange-600' : 'bg-gray-200 text-gray-400'
          }`}
          style={{ minWidth: '14px', textAlign: 'center' }}
        >
          {docCount}
        </span>
      </button>

      {/* Tooltip */}
      {showTooltip && (
        <div className="absolute right-0 top-full mt-1.5 z-50 bg-white shadow-md rounded-md px-2 py-1.5 min-w-[120px] max-w-[180px] pointer-events-none">
          {hasDocuments ? (
            <div className="space-y-0.5">
              {docNames?.slice(0, 3)?.map((name, i) => (
                <p key={i} className="text-[10px] text-gray-600 truncate">{name}</p>
              ))}
              {docCount > 3 && (
                <p className="text-[10px] text-gray-400">{t('extras.rowActions.moreDocuments', { count: docCount - 3 })}</p>
              )}
            </div>
          ) : (
            <p className="text-[10px] text-gray-500">{t('extras.rowActions.noDocuments')}</p>
          )}
        </div>
      )}
    </div>
  );
};

// Interactive status pill with dropdown
const StatusPill = ({ extra, statusConfig, onStatusChange, t }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const cfg = statusConfig?.[extra?.status] || statusConfig?.draft;

  // Case A: approved + signed avenant → fully locked, static pill
  if (extra?.status === 'approved' && extra?.hasSignedAvenant) {
    return (
      <Badge
        variant={statusVariant('extras', extra?.status)}
        className="cursor-default select-none"
        title={t('extras.locked') || 'Locked — signed avenant exists'}
      >
        {cfg?.label}
        <Icon name="Lock" size={9} className="ml-0.5 opacity-60" />
      </Badge>
    );
  }

  // Case B: approved + no signed avenant → revert-only dropdown (submitted / draft)
  if (extra?.status === 'approved' && !extra?.hasSignedAvenant) {
    const REVERT_OPTIONS = ['submitted', 'draft'];
    return (
      <div className="relative">
        <Badge
          as="button"
          ref={ref}
          variant={statusVariant('extras', extra?.status)}
          onClick={() => setOpen((v) => !v)}
          className="cursor-pointer select-none transition-opacity hover:opacity-80"
          title={t('extras.rowActions.revertStatus')}
        >
          {cfg?.label}
          <Icon name="ChevronDown" size={10} />
        </Badge>
        <PortalDropdown
          anchorRef={ref}
          open={open}
          onClose={() => setOpen(false)}
          align="left"
          width={140}
          items={REVERT_OPTIONS?.map((status) => ({
            label: statusConfig?.[status]?.label,
            dot: BADGE_VARIANTS?.[statusVariant('extras', status)]?.dot,
            onClick: () => onStatusChange(extra?.id, status),
          }))}
        />
      </div>
    );
  }

  // Case C: any other status → normal full dropdown
  return (
    <div className="relative">
      <Badge
        as="button"
        ref={ref}
        variant={statusVariant('extras', extra?.status)}
        onClick={() => setOpen((v) => !v)}
        className="cursor-pointer select-none transition-opacity hover:opacity-80"
        title={t('extras.rowActions.changeStatus')}
      >
        {cfg?.label}
        <Icon name="ChevronDown" size={10} />
      </Badge>
      <PortalDropdown
        anchorRef={ref}
        open={open}
        onClose={() => setOpen(false)}
        align="left"
        width={140}
        items={STATUS_OPTIONS?.map((status) => ({
          label: statusConfig?.[status]?.label,
          dot: BADGE_VARIANTS?.[statusVariant('extras', status)]?.dot,
          active: extra?.status === status,
          onClick: () => { if (extra?.status !== status) onStatusChange(extra?.id, status); },
        }))}
      />
    </div>
  );
};

// ─── Ordre de changement (ODC) du client ──────────────────────────────────────
// Meme mecanique que la pastille de statut : pastille-bouton + menu.
//   non approuve        → « — »
//   approuve + interne    → « Non requis » (gris, sans menu)
//   approuve + en attente → ambre « En attente ▾ » : « ODC recu… » (televersement obligatoire)
//   approuve + recu      → vert « Recu ▾ » (date au survol) : « Remettre en attente »
const CoPill = ({ extra, onMarkReceived, onResetPending }) => {
  const { t, language } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  if (extra?.status !== 'approved') {
    return <span className="block text-center text-xs text-gray-400">—</span>;
  }
  // Categorie interne : aucun ODC attendu. L'affichage suit la categorie, meme
  // si une ancienne ligne porte encore co_status = 'pending'.
  if (!isCoRequired(extra?.category)) {
    return <span className="text-xs text-gray-400">{t('extras.co.notRequired')}</span>;
  }

  const received = extra?.coStatus === 'received';
  const items = received
    ? [{ label: t('extras.co.resetPending'), icon: 'RotateCcw', onClick: () => onResetPending?.(extra) }]
    : [{ label: t('extras.co.markReceived'), icon: 'Upload', onClick: () => onMarkReceived?.(extra) }];

  return (
    <div className="relative">
      <Badge
        as="button"
        ref={ref}
        variant={received ? 'green' : 'amber'}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="cursor-pointer select-none transition-opacity hover:opacity-80"
        title={received && extra?.coReceivedAt
          ? t('extras.co.receivedOn', { date: formatDate(extra?.coReceivedAt, language) })
          : t('extras.co.menuLabel')}
      >
        {received ? t('extras.co.received') : t('extras.co.pending')}
        <Icon name="ChevronDown" size={10} />
      </Badge>
      <PortalDropdown
        anchorRef={ref}
        open={open}
        onClose={() => setOpen(false)}
        align="left"
        width={200}
        items={items}
      />
    </div>
  );
};

const ExtrasTable = ({ extras, sortConfig, onSort, onStatusChange, onDelete, onEdit, coMap, highlightExtraId, selectedIds, onToggleRow, onToggleAll, onDownloadPDF, directiveMap = {}, onOpenDirective, onCoChanged, onNotify, onView }) => {
  const { t } = useTranslation();
  const { id: projectId } = useParams();
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [selectedExtra, setSelectedExtra] = useState(null);
  const [isDocumentsOpen, setIsDocumentsOpen] = useState(false);

  const STATUS_CONFIG = getStatusConfig(t);
  const TYPE_CONFIG = getTypeConfig(t);
  const CATEGORY_CONFIG = getCategoryConfig(t);
  // Sous-ligne de la Description : sous-traitants de l'extra et leurs montants.
  // Une seule requete pour toutes les lignes du tableau ; relue quand un extra
  // change (montant ou date de mise a jour).
  const [linesByExtra, setLinesByExtra] = useState({});
  const [orgName, setOrgName] = useState('');
  useEffect(() => {
    getCurrentOrgName()?.then((name) => { if (name) setOrgName(name); })?.catch(() => {});
  }, []);
  const linesKey = (extras || [])?.map((e) => `${e?.id}:${e?.updatedAt || ''}:${e?.amount ?? ''}`)?.join('|');
  useEffect(() => {
    const ids = (extras || [])?.map((e) => e?.id)?.filter(Boolean);
    if (!ids?.length) { setLinesByExtra({}); return undefined; }
    let alive = true;
    supabase
      ?.from('project_extra_lines')
      ?.select('extra_id, amount, is_internal, subcontract_id, subcontract:subcontracts(subcontractors(company_name))')
      ?.in('extra_id', ids)
      ?.then(({ data, error }) => {
        if (!alive || error) return;
        const map = {};
        (data || [])?.forEach((l) => {
          const key = l?.is_internal ? '__internal__' : (l?.subcontract_id || '__none__');
          const name = l?.is_internal ? null : (l?.subcontract?.subcontractors?.company_name || null);
          const groups = (map[l?.extra_id] ||= []);
          const g = groups?.find((x) => x?.key === key);
          if (g) g.amount += Number(l?.amount) || 0;
          else groups?.push({ key, name, internal: !!l?.is_internal, amount: Number(l?.amount) || 0 });
        });
        setLinesByExtra(map);
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linesKey]);

  // « Voltam inc. 4 261,05 $ · Gypse Rive-Nord 1 200,00 $ » (signe moins pour un credit)
  const linesSummary = (extra) => {
    // Les lignes a 0 $ (souvent vides) n'apportent rien : on les saute.
    const groups = linesByExtra?.[extra?.id]?.filter((g) => Math.abs(g?.amount) > 0);
    if (!groups?.length) return null;
    // Lignes stockees relatives au type (parfois negatives) : vrai signe ici.
    const typeSign = extra?.type === 'credit' ? -1 : 1;
    return groups?.map((g) => {
      const label = g?.internal
        ? (orgName ? `${orgName} ${t('internal.suffix')}` : t('internal.suffix'))
        : (g?.name || '—');
      const real = typeSign * (Number(g?.amount) || 0);
      return `${label} ${real < 0 ? '−' : ''}${fmtCAD(Math.abs(real))}`;
    })?.join(' · ');
  };

  // Notes internes : compteur + derniere note de tous les extras du projet, en
  // une requete ; relue apres chaque ajout / modification / suppression.
  const [notesSummary, setNotesSummary] = useState({});
  const [notesVersion, setNotesVersion] = useState(0);
  useEffect(() => {
    if (!projectId) return undefined;
    let alive = true;
    listNotesSummary(projectId)
      ?.then((summary) => { if (alive) setNotesSummary(summary || {}); })
      ?.catch((err) => console.error('[extra_notes] resume impossible:', err));
    return () => { alive = false; };
  }, [projectId, notesVersion]);

  // Pieces : compteurs + noms de tous les extras du projet (table
  // extra_documents, une requete) ; relus apres chaque ajout / suppression.
  const [docsVersion, setDocsVersion] = useState(0);
  const [docsSummary, setDocsSummary] = useState({});
  useEffect(() => {
    if (!projectId) return undefined;
    let alive = true;
    listDocumentCounts('extra', projectId)?.then((summary) => { if (alive && summary) setDocsSummary(summary); });
    return () => { alive = false; };
  }, [projectId, docsVersion]);
  // ODC : extra en cours de reception (televersement obligatoire).
  const [coUploadExtra, setCoUploadExtra] = useState(null);
  const coSavingRef = useRef(false);
  const [coResetExtra, setCoResetExtra] = useState(null);
  const [coResetSaving, setCoResetSaving] = useState(false);

  const handleDeleteClick = (id) => setPendingDeleteId(id);

  const handleConfirmDelete = () => {
    if (pendingDeleteId != null) onDelete(pendingDeleteId);
    setPendingDeleteId(null);
  };

  const handleCancelDelete = () => setPendingDeleteId(null);

  // « ODC recu… » : ouvre les pieces de l'extra (meme panneau, meme table).
  const handleCoMarkReceived = (extra) => {
    coSavingRef.current = false;
    setCoUploadExtra(extra);
  };

  // Document OBLIGATOIRE : l'ODC ne passe a « Recu » qu'apres un televersement
  // REUSSI (ligne extra_documents creee). Fermer sans televerser : rien.
  // Le passage a « Recu » se fait au 1er fichier reussi ; la modale se ferme a
  // la fin du lot (onCountChange), pour ne pas couper un envoi en cours.
  const handleCoUploaded = async () => {
    const extra = coUploadExtra;
    if (!extra?.id || coSavingRef.current) return;
    try {
      coSavingRef.current = true;
      await projectExtrasService?.markCoReceived(extra?.id);
      onNotify?.('success', t('extras.co.receivedToast'));
      await onCoChanged?.();
    } catch (err) {
      console.error('ODC: passage a recu impossible', err);
      coSavingRef.current = false;
      onNotify?.('error', t('extras.co.error'));
    }
  };

  const handleCoResetConfirm = async () => {
    if (!coResetExtra?.id) return;
    setCoResetSaving(true);
    try {
      await projectExtrasService?.resetCoPending(coResetExtra?.id);
      setCoResetExtra(null);
      onNotify?.('success', t('extras.co.resetToast'));
      await onCoChanged?.();
    } catch (err) {
      console.error('ODC: remise en attente impossible', err);
      onNotify?.('error', t('extras.co.error'));
    } finally {
      setCoResetSaving(false);
    }
  };

  const formatCurrency = (val) => fmtCAD(val);

  // Checkbox state helpers
  const allIds = extras?.map(e => e?.id) || [];
  const allSelected = allIds?.length > 0 && allIds?.every(id => selectedIds?.has(id));
  const someSelected = allIds?.some(id => selectedIds?.has(id)) && !allSelected;

  const SortableHeader = ({ column, label, align = 'left', nowrap = true, width, className = '' }) => (
    <th
      className={`text-${align} text-xs font-medium text-gray-500 px-3 py-3 cursor-pointer select-none bg-gray-50 hover:bg-gray-100 ${className}`}
      style={{ whiteSpace: nowrap ? 'nowrap' : undefined, width: width || undefined }}
      onClick={() => onSort(column)}
    >
      <div className={`flex items-center gap-1 ${align === 'right' ? 'justify-end' : ''}`}>
        <span>{label}</span>
        <SortIcon column={column} sortConfig={sortConfig} />
      </div>
    </th>
  );

  if (extras?.length === 0) {
    return (
      <div className="p-12 text-center">
        <Icon name="FileX" size={40} className="mx-auto mb-3 text-gray-300" />
        <p className="text-sm text-gray-500">{t('extras.empty.noExtras')}</p>
        <p className="text-xs text-gray-400 mt-1">{t('extras.empty.clickToAdd')}</p>
      </div>
    );
  }

  return (
    <>
      {pendingDeleteId != null && (
        <ConfirmDialog
          isOpen={pendingDeleteId != null}
          title={t('extras.deleteDialog.title')}
          message={t('extras.deleteDialog.message')}
          confirmVariant="danger"
          confirmLabel={t('extras.deleteDialog.delete')}
          cancelLabel={t('extras.deleteDialog.cancel')}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      )}
      <EntityDocumentsModal
        isOpen={isDocumentsOpen}
        onClose={() => { setIsDocumentsOpen(false); setSelectedExtra(null); }}
        entityType="extra"
        entityId={selectedExtra?.id}
        projectId={projectId}
        subtitle={selectedExtra ? `${selectedExtra?.title}${selectedExtra?.coNumber ? ` · ${selectedExtra?.coNumber}` : ''}` : ''}
        onCountChange={() => setDocsVersion((v) => v + 1)}
      />
      {/* ODC recu : televersement du document, obligatoire */}
      <EntityDocumentsModal
        isOpen={!!coUploadExtra}
        onClose={() => setCoUploadExtra(null)}
        entityType="extra"
        entityId={coUploadExtra?.id}
        projectId={projectId}
        subtitle={coUploadExtra ? `${coUploadExtra?.title || ''} — ${t('extras.co.docRequired')}` : ''}
        onCountChange={() => {
          setDocsVersion((v) => v + 1);
          if (coSavingRef.current) setCoUploadExtra(null);
        }}
        onUploaded={handleCoUploaded}
      />
      {coResetExtra && (
        <ConfirmDialog
          isOpen={!!coResetExtra}
          title={t('extras.co.resetTitle')}
          message={t('extras.co.resetMessage', { title: coResetExtra?.title || '—' })}
          confirmLabel={t('extras.co.resetPending')}
          cancelLabel={t('common.cancel')}
          loading={coResetSaving}
          onConfirm={handleCoResetConfirm}
          onCancel={() => { if (!coResetSaving) setCoResetExtra(null); }}
        />
      )}
      <div>
        {/* Largeurs fixes : la Description prend le reste et se tronque, le
            tableau ne deborde plus (mesure : 1 280 et 1 470 px). */}
        <table className="w-full table-fixed">
          <colgroup>
            <col style={{ width: '36px' }} />
            <col style={{ width: '76px' }} />
            <col style={{ width: '116px' }} />
            <col style={{ width: '112px' }} />
            <col />
            <col style={{ width: '100px' }} />
            <col style={{ width: '100px' }} />
            <col style={{ width: '160px' }} />
            <col style={{ width: '120px' }} />
            <col style={{ width: '108px' }} />
          </colgroup>
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr className="border-b border-gray-200">
              {/* Select-all checkbox */}
              <th className="px-3 py-3 text-center bg-gray-50" style={{ width: '36px' }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={el => { if (el) el.indeterminate = someSelected; }}
                  onChange={() => onToggleAll?.(allIds, allSelected)}
                  className="w-3.5 h-3.5 rounded border-gray-300 text-primary focus:ring-2 focus:ring-primary/20 cursor-pointer accent-primary"
                  title={t('extras.table.selectAll')}
                />
              </th>
              <SortableHeader column="type" label={t('extras.table.type')} width="76px" />
              <SortableHeader column="directive_nomenclature" label={t('extras.table.co')} width="116px" />
              <SortableHeader column="category" label={t('extras.category.label')} width="112px" />
              <th
                className="text-left text-xs font-medium text-gray-500 px-3 py-3 cursor-pointer select-none bg-gray-50 hover:bg-gray-100"
                onClick={() => onSort('title')}
              >
                <div className="flex items-center gap-1">
                  <span>{t('extras.table.description')}</span>
                  <SortIcon column="title" sortConfig={sortConfig} />
                </div>
              </th>
              <SortableHeader column="amount" label={t('extras.table.submitted')} align="right" width="100px" />
              <SortableHeader column="cost_accepted" label={t('extras.table.accepted')} align="right" width="100px" />
              <SortableHeader column="status" label={t('extras.table.status')} width="160px" />
              <th className="text-left text-xs font-medium text-gray-500 px-3 py-3 bg-gray-50" style={{ whiteSpace: 'nowrap', width: '120px' }} title={t('extras.co.menuLabel')}>{t('extras.table.changeOrder')}</th>
              <th className="text-left text-xs font-medium text-gray-500 px-3 py-3 bg-gray-50" style={{ whiteSpace: 'nowrap', width: '108px' }}>{t('extras.table.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {extras?.map((extra) => {
              const typeCfg = TYPE_CONFIG?.[extra?.type] || TYPE_CONFIG?.extra;
              const categoryCfg = extra?.category ? CATEGORY_CONFIG?.[extra?.category] : null;
              const isHighlighted = highlightExtraId && extra?.id === highlightExtraId;
              const isSelected = selectedIds?.has(extra?.id);
              const isCredit = extra?.type === 'credit';
              const acceptedNum = parseFloat(extra?.costAccepted);
              const acceptedIsNegative = isCredit || acceptedNum < 0;

              return (
                <tr
                  key={extra?.id}
                  id={`extra-row-${extra?.id}`}
                  // Clic sur la ligne : ouvre la fiche (consultation). Les controles
                  // de la ligne (case, pastilles, pieces, menu) gardent leur role.
                  onClick={(e) => {
                    if (!onView) return;
                    if (e?.target?.closest?.('button, a, input, select, textarea, label, [role="menu"], [role="menuitem"]')) return;
                    onView(extra);
                  }}
                  className={`transition-colors ${onView ? 'cursor-pointer' : ''} ${
                    isSelected
                      ? 'bg-orange-50 hover:bg-orange-100/70'
                      : isHighlighted
                        ? 'bg-orange-50 ring-2 ring-inset ring-primary/40'
                        : 'hover:bg-gray-50/60'
                  }`}
                >
                  {/* Row checkbox */}
                  <td className="px-3 py-3 text-center align-middle" style={{ width: '36px' }}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onToggleRow?.(extra?.id)}
                      className="w-3.5 h-3.5 rounded border-gray-300 text-primary focus:ring-2 focus:ring-primary/20 cursor-pointer accent-primary"
                    />
                  </td>
                  {/* Type pill */}
                  <td className="px-3 py-3 align-middle" style={{ whiteSpace: 'nowrap' }}>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium leading-tight ${typeCfg?.bg} ${typeCfg?.text}`}>
                      {typeCfg?.label}
                    </span>
                  </td>
                  {/* O.C. — la directive LIEE, cliquable. co_number n'est plus
                      affiche ici : il reste le numero echange avec le client,
                      la directive est la reference interne. */}
                  <td className="px-3 py-3 align-middle overflow-hidden" style={{ whiteSpace: 'nowrap' }}>
                    {(() => {
                      const directive = extra?.directiveId ? directiveMap?.[extra?.directiveId] : null;
                      if (!directive?.nomenclature) {
                        // Sans directive liee : le n° de directive saisi a la
                        // main (co_number, ex. « Interne ») s'affiche tel quel.
                        if (extra?.coNumber?.trim()) {
                          return (
                            <span className="inline-flex items-center max-w-full px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600" title={extra?.coNumber}>
                              <span className="truncate">{extra?.coNumber}</span>
                            </span>
                          );
                        }
                        return <span className="text-xs text-gray-400">—</span>;
                      }
                      return (
                        <button
                          type="button"
                          onClick={() => onOpenDirective?.(directive?.id)}
                          title={directive?.description || directive?.title || undefined}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-orange-100 text-orange-600 cursor-pointer transition-colors hover:bg-orange-200 hover:text-orange-700 focus:outline-none focus:ring-2 focus:ring-primary/20"
                        >
                          {directive?.nomenclature}
                        </button>
                      );
                    })()}
                  </td>
                  {/* Categorie */}
                  <td className="px-3 py-3 align-middle overflow-hidden" style={{ whiteSpace: 'nowrap' }}>
                    {categoryCfg
                      ? (
                        <span
                          className={`inline-flex items-center max-w-full px-2 py-0.5 rounded-full text-[11px] font-medium leading-tight ${categoryCfg?.bg} ${categoryCfg?.text}`}
                          title={categoryCfg?.label}
                        >
                          <span className="truncate">{categoryCfg?.label}</span>
                        </span>
                      )
                      : <span className="text-xs text-gray-400">—</span>
                    }
                  </td>
                  {/* Description (was Title) + sous-ligne : sous-traitants et montants */}
                  <td className="px-3 py-3 align-middle min-w-0">
                    <p className="text-xs text-gray-700 whitespace-nowrap overflow-hidden text-ellipsis leading-tight" title={extra?.title || undefined}>
                      {extra?.title}
                    </p>
                    {linesSummary(extra) && (
                      <p className="text-[10px] text-gray-400 leading-tight truncate mt-0.5" title={linesSummary(extra)}>
                        {linesSummary(extra)}
                      </p>
                    )}
                  </td>
                  {/* Submitted (was Submitted Cost / amount) */}
                  <td className="px-3 py-3 text-right align-middle" style={{ whiteSpace: 'nowrap' }}>
                    <span className="text-xs text-gray-900 leading-tight">
                      {(() => {
                        const baseAmount = parseFloat(extra?.costSubmitted ?? extra?.amount) || 0;
                        const profit = parseFloat(extra?.profit) || 0;
                        const submittedTotal = baseAmount + profit;
                        return <>{isCredit ? '-' : '+'}{formatCurrency(submittedTotal)}</>;
                      })()}
                    </span>
                  </td>
                  {/* Accepted (was Cost Accepted) — financial color logic */}
                  <td className="px-3 py-3 text-right align-middle" style={{ whiteSpace: 'nowrap' }}>
                    {extra?.status === 'approved' && extra?.costAccepted != null
                      ? (() => {
                          // Le signe vient du TYPE, jamais de la valeur : les montants
                          // sont stockes positifs (migration 20260310170000, « type
                          // carries sign semantics »). Un credit affichait donc
                          // « +1 000,00 $ » en vert, juste sous « -1 000,00 $ ».
                          // Math.abs : une vieille ligne deja negative ne doit pas
                          // recevoir le signe deux fois.
                          const val = Math.abs(parseFloat(extra?.costAccepted) || 0);
                          if (val === 0) {
                            return <span className="text-xs font-semibold leading-tight text-gray-400">{formatCurrency(0)}</span>;
                          }
                          const colorClass = acceptedIsNegative ? 'text-red-600' : 'text-green-600';
                          return (
                            <span className={`text-xs font-semibold leading-tight ${colorClass}`}>
                              {acceptedIsNegative ? '-' : '+'}{formatCurrency(val)}
                            </span>
                          );
                        })()
                      : <span className="text-xs text-gray-400">—</span>
                    }
                  </td>
                  {/* Status */}
                  <td className="px-3 py-3 align-middle" style={{ whiteSpace: 'nowrap' }}>
                    <StatusPill
                      extra={extra}
                      statusConfig={STATUS_CONFIG}
                      onStatusChange={onStatusChange}
                      t={t}
                    />
                  </td>
                  {/* Ordre de changement (ODC) du client */}
                  <td className="px-3 py-3 align-middle" style={{ whiteSpace: 'nowrap' }}>
                    <CoPill
                      extra={extra}
                      onMarkReceived={handleCoMarkReceived}
                      onResetPending={setCoResetExtra}
                    />
                  </td>
                  {/* Actions */}
                  <td className="px-3 py-3 align-middle" style={{ whiteSpace: 'nowrap' }}>
                    <div className="flex items-end justify-end gap-2 h-full">
                      <div className="relative flex items-end">
                        <DocumentIconButton
                          summary={docsSummary?.[extra?.id]}
                          onClick={() => { setSelectedExtra(extra); setIsDocumentsOpen(true); }}
                        />
                      </div>
                      <div className="relative flex items-end">
                        <ExtraNotesButton
                          extra={extra}
                          projectId={projectId}
                          number={(extra?.directiveId ? directiveMap?.[extra?.directiveId]?.nomenclature : null) || extra?.coNumber || null}
                          summary={notesSummary?.[extra?.id]}
                          onChanged={() => setNotesVersion((v) => v + 1)}
                        />
                      </div>
                      <div className="flex items-end">
                        <ActionsMenu
                          extra={extra}
                          onEdit={onEdit}
                          onDelete={handleDeleteClick}
                          onViewDocuments={(e) => { setSelectedExtra(e); }}
                          onDownloadPDF={onDownloadPDF}
                          t={t}
                        />
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default ExtrasTable;
